import { useState } from "react";
import React, { Fragment } from "react";
import { motion, AnimatePresence } from "framer-motion";
// Removed Dumbbell and Target imports
import { ChevronDown, ChevronUp } from "lucide-react"; 
import { Dialog, Transition } from "@headlessui/react";

/**
 * Props:
 * skills, combineScores, todaysSCWorkouts, workouts, totalProgramScore
 * onGenerateWorkout(minutes)
 */
export default function RightSidebar({
  skills = [],
  combineScores = [],
  // S&C Drills
  todaysSCWorkouts = [], 
  // Skill Drills (Assumed to be from workout_drills table)
  workouts = [], 
  totalProgramScore = 0,
  onGenerateWorkout = async () => { },
}) {
  const [open, setOpen] = useState({
    programScore: true,
    skills: true,
    combine: false,
    generate: false,
    todays: true,
  });

  const [mobileOpen, setMobileOpen] = useState(false);

  const toggle = (k) => setOpen((s) => ({ ...s, [k]: !s[k] }));

  const Panel = ({ title, openKey, children, subtitle }) => (
    <div className="border rounded-lg p-3 bg-white">
      <div
        className="flex justify-between items-center cursor-pointer"
        onClick={() => toggle(openKey)}
      >
        <div>
          <h4 className="font-bold">{title}</h4>
          {subtitle && <p className="text-sm text-gray-500">{subtitle}</p>}
        </div>
        <div>{open[openKey] ? <ChevronUp /> : <ChevronDown />}</div>
      </div>
      <AnimatePresence initial={false}>
        {open[openKey] && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.18 }}
          >
            <div className="mt-3">{children}</div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );

  // NOTE: The grouping logic is no longer used for display, but kept in case it's needed elsewhere.
  // const groupedSkillDrills = workouts.reduce((acc, drill) => {
  //   const block = drill.workout_blocks || "Uncategorized";
  //   if (!acc[block]) {
  //     acc[block] = [];
  //   }
  //   acc[block].push(drill);
  //   return acc;
  // }, {});

  const totalDrillCount = workouts.length + todaysSCWorkouts.length;
  // const skillBlockKeys = Object.keys(groupedSkillDrills).sort();


  return (
    <>
      {/* Desktop / Tablet Sidebar */}
      <aside className="hidden lg:flex w-[360px] min-w-[320px] flex-col gap-4">
        <div className="flex items-center justify-between bg-white border border-blue-100 rounded-xl px-3 py-3 shadow-inner">
          {/* Left side: label */}
          <div className="text-md font-bold text-black-600">
            Program Readiness
          </div>

          {/* Right side: total score */}
          <div className="text-xl font-extrabold text-blue-600">
            {totalProgramScore}%
          </div>
        </div>

        {/* 🏆 TODAY'S WORKOUTS PANEL: REVISED DISPLAY LOGIC (Flat List for Skill Drills) */}
        <Panel title="Today's Workouts" openKey="todays" subtitle={`${totalDrillCount} drills`}>
          {totalDrillCount === 0 ? (
            <p className="text-sm text-gray-400">No workouts today.</p>
          ) : (
            <div className="space-y-4">
              {/* === SKILL WORKOUTS (Group 1: Flat List) === */}
              {workouts.length > 0 && (
                <div>
                  <h5 className="text-sm font-semibold text-gray-700 mb-2">
                    Skill Drills
                  </h5>
                  <div className="space-y-2">
                    {/* NEW: Map directly over workouts and apply S&C card style */}
                    {workouts.map((drill) => (
                        <div key={drill.id} className="p-3 rounded bg-white border shadow-sm">
                            <div className="flex justify-between items-center">
                                <div className="font-medium text-sm text-gray-800">{drill.drill_name}</div>
                                <div className="text-xs text-gray-600">
                                    {drill.duration ? `${drill.duration} min` : `${drill.sets || "—"}x${drill.reps || "—"}`}
                                </div>
                            </div>
                            {/* Display the workout block (formerly used for grouping) and description */}
                            <div className="text-xs text-gray-500 mt-0.5">
                                {drill.workout_blocks && <span className="font-semibold">{drill.workout_blocks}</span>}
                                {drill.workout_blocks && drill.description && <span className="mx-1 text-gray-400">•</span>}
                                {drill.description}
                            </div>
                        </div>
                    ))}
                  </div>
                </div>
              )}

              {/* === S&C DRILLS (Group 2) === */}
              {todaysSCWorkouts.length > 0 && (
                <div>
                  {workouts.length > 0 && <hr className="my-4 border-gray-200" />} 
                  <h5 className="text-sm font-semibold text-gray-700 mb-2">
                    S&C Drills
                  </h5>
                  <div className="space-y-2">
                    {/* Existing S&C layout */}
                    {todaysSCWorkouts.map((s) => (
                      <div key={s.id} className="p-3 rounded bg-white border shadow-sm">
                        <div className="flex justify-between items-center">
                          <div className="font-medium text-sm text-gray-800">{s.drill_name}</div>
                          <div className="text-xs text-gray-600">
                            {s.duration_or_reps || "—"} {(s.duration_or_reps && s.duration_or_reps.includes('s'))}
                          </div>
                        </div>
                        {s.description && <div className="text-xs text-gray-500 mt-1">{s.description}</div>}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </Panel>
      </aside>

      <Transition appear show={mobileOpen} as={Fragment}>
        <Dialog as="div" className="relative z-50" onClose={() => setMobileOpen(false)}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-200"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-150"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black bg-opacity-30" />
          </Transition.Child>

          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-end justify-center p-4 text-center">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-200"
                enterFrom="opacity-0 translate-y-4"
                enterTo="opacity-100 translate-y-0"
                leave="ease-in duration-150"
                leaveFrom="opacity-100 translate-y-0"
                leaveTo="opacity-0 translate-y-4"
              >
                <Dialog.Panel className="w-full max-w-md transform overflow-hidden rounded-t-xl bg-white p-6 text-left align-middle shadow-xl transition-all">
                  <Dialog.Title as="h3" className="text-lg font-medium leading-6 text-gray-900">
                    Panels
                  </Dialog.Title>
                  <div className="mt-4 space-y-4">
                    {/* Program Score */}
                    <div>
                      <h4 className="font-semibold">Program Score</h4>
                      <div className="text-xl font-extrabold text-black-600">{totalProgramScore}%</div>
                    </div>
                    
                    {/* Today's Workouts - Mobile View (Icons/Color Removed) */}
                    <div>
                      <h4 className="font-semibold">Today's Workouts</h4>
                      <div className="mt-2 space-y-2">
                        {workouts.length > 0 && (
                            <>
                                <h5 className="text-sm font-medium text-gray-700">Skill Drills ({workouts.length})</h5>
                                {workouts.map((w) => <div key={w.id} className="text-sm pl-2 border-l-2 border-gray-200">{w.drill_name}</div>)}
                            </>
                        )}
                        {todaysSCWorkouts.length > 0 && (
                            <>
                                <h5 className="text-sm font-medium text-gray-700 pt-2">S&C Drills ({todaysSCWorkouts.length})</h5>
                                {todaysSCWorkouts.map((s) => <div key={s.id} className="text-sm pl-2 border-l-2 border-gray-200">{s.drill_name} ({s.duration_or_reps})</div>)}
                            </>
                        )}
                        {workouts.length + todaysSCWorkouts.length === 0 && <div className="text-sm text-gray-400">No workouts today.</div>}
                      </div>
                    </div>
                  </div>

                  <div className="mt-4">
                    <button onClick={() => setMobileOpen(false)} className="w-full py-2 rounded bg-gray-100">Close</button>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>
    </>
  );
}